import { useState } from 'react'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { VideoIcon, ImageIcon } from '@radix-ui/react-icons'
import { ToolbarSplitButton, DropdownOption } from './ToolbarSplitButton'
import { UploadPhotoButton } from '../UploadPhotoButton'
import { UploadVideoButton } from '../UploadVideoButton'
import { ImagePlaceholder } from './DraggablePlaceholders/ImagePlaceholder'
import { DocumentTypeChoices } from '@/lib/api/client'
import { useDocumentState } from '@/state'
import { useCADQuery } from '@/services/queries/cads'
import { useIsReadOnly } from '@/pages/CADPage/hooks/useIsReadOnly'
import { useAnnotationLayer } from '../Annotations/queries'

type MediaMenuProps = {
  isDisabled: boolean
  documentType?: DocumentTypeChoices
}

export const MediaMenu = ({ isDisabled, documentType }: MediaMenuProps) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [videoDialogOpen, setVideoDialogOpen] = useState(false)

  const selectedPageId = useDocumentState((state) => state.currentPageInView)
  const startAddingElement = useDocumentState(
    (state) => state.startAddingElement,
  )
  const { data: cadData } = useCADQuery()
  const documentPage = cadData?.documentPages.find(
    (page) => page.id === selectedPageId,
  )
  const { isLoading: readOnlyLoading, data: readOnlyData } = useIsReadOnly()
  const { maxZIndex } = useAnnotationLayer(selectedPageId || '')

  const disableAddImage =
    !documentPage || readOnlyLoading || readOnlyData.isReadOnly

  const handleUpload = () => {
    setImageDialogOpen(true)
  }

  const handleVideo = () => {
    setVideoDialogOpen(true)
  }

  const handlePlaceholder = (e: React.MouseEvent) => {
    if (!documentPage) return

    const baseUrl = window.location.origin
    const src = `${baseUrl}/static/images/cad-placeholder.png`

    startAddingElement({
      newElementData: {
        Component: () => <ImagePlaceholder src={src} />,
        dataType: 'view',
        attributes: {
          viewId: null,
          imagePosition: 'absolute',
        },
        documentPageId: documentPage.id as string,
      },
      triggerLocation: {
        x: e.clientX,
        y: e.clientY,
      },
    })
  }

  const defaultOption: DropdownOption = {
    title: 'Image',
    description: 'Add an image to the document',
    Icon: ImageIcon,
    handler: handleUpload,
    disabled: disableAddImage,
  }

  const dropdownOptions: DropdownOption[] = [
    {
      title: 'Video',
      description: 'Add a video to the document',
      Icon: VideoIcon,
      handler: handleVideo,
      disabled: disableAddImage,
    },
    {
      title: 'Placeholder image',
      description: 'Add a placeholder CAD image to the document',
      Icon: ImageIcon,
      handler: handlePlaceholder,
      disabled: disableAddImage,
    },
  ]

  return (
    <>
      {documentPage && (
        <>
          <UploadPhotoButton
            documentPage={documentPage}
            documentType={documentType}
            open={imageDialogOpen}
            onOpenChange={setImageDialogOpen}
            renderTrigger={false}
            zIndex={maxZIndex + 3}
          />
          <UploadVideoButton
            documentPage={documentPage}
            open={videoDialogOpen}
            onOpenChange={setVideoDialogOpen}
            renderTrigger={false}
            zIndex={maxZIndex + 3}
          />
        </>
      )}
      <ToolbarSplitButton
        Icon={ArrowUpTrayIcon}
        defaultAction={handleUpload}
        defaultOption={defaultOption}
        dropdownOptions={dropdownOptions}
        tooltip="Upload an image"
        isDisabled={isDisabled || disableAddImage}
      />
    </>
  )
}
